/**
 * Shared utilities for text highlighting functionality
 */

import { diffWords } from 'diff';

/**
 * Normalizes quote/apostrophe punctuation variants to their straight ASCII form
 * so that curly-vs-straight punctuation differences are not treated as changes.
 * Mirrors the backend `normalizePunctuationForComparison` (string.helper.ts) —
 * keep the two in sync.
 */
export const normalizePunctuationForComparison = (
  text?: string | null,
): string => {
  if (!text) return '';

  return text
    .replace(/[“”„‟″]/g, '"')
    .replace(/[‘’‚‛′]/g, '\u0027')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;|&apos;/g, '\u0027');
};

/**
 * Common highlight colors and styles
 */
export const HIGHLIGHT_STYLES = {
  added: {
    backgroundColor: '#CADCD2',
    color: '#333',
    padding: '1px 2px',
    borderRadius: '2px',
    boxDecorationBreak: 'clone',
    WebkitBoxDecorationBreak: 'clone',
  },
  removed: {
    backgroundColor: '#ffcdd2',
    color: '#333',
    textDecoration: 'line-through',
    padding: '1px 2px',
    borderRadius: '2px',
    boxDecorationBreak: 'clone',
    WebkitBoxDecorationBreak: 'clone',
  },
} as const;

/**
 * CSS class names for HTML highlighting
 */
export const HIGHLIGHT_CLASSES = {
  added: 'highlight-added',
  removed: 'highlight-removed',
} as const;

/**
 * Common container styles
 */
export const CONTAINER_STYLES = {
  lineHeight: 1.5,
  wordWrap: 'break-word' as const,
  fontSize: 'inherit',
  color: 'inherit',
};

/**
 * Type definition for text segments with highlighting information
 */
export type HighlightSegment = {
  text: string;
  isChanged: boolean;
  type: 'added' | 'removed' | 'unchanged';
};

export type TextDiff = {
  value: string;
  added?: boolean;
  removed?: boolean;
  count?: number;
};

const BLOCK_LEVEL_CLOSE_TAG_PATTERN =
  /<\/(p|div|li|ul|ol|h[1-6]|tr|td|th|blockquote|pre)>/gi;
const BR_TAG_PATTERN = /<br\s*\/?>/gi;

const insertBlockSeparators = (html: string): string =>
  html
    .replace(BLOCK_LEVEL_CLOSE_TAG_PATTERN, '$& ')
    .replace(BR_TAG_PATTERN, '$& ');

/**
 * Extracts normalized plain text from HTML content.
 * Inserts spaces after block-level closing tags and `<br>` tags so words from
 * adjacent elements stay separate during text comparison, then collapses all
 * whitespace runs to a single space and trims leading/trailing whitespace.
 */
export const extractTextFromHtml = (html: string): string => {
  const withSeparators = insertBlockSeparators(html);

  if (typeof document === 'undefined') {
    return withSeparators
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = withSeparators;
  const text = tempDiv.textContent || tempDiv.innerText || '';
  return text.replace(/\s+/g, ' ').trim();
};

/**
 * Compare two texts and return highlighted segments for the new text
 * @param originalText - The original text
 * @param newText - The new/customized text
 * @param useWordDiff - Whether to use word-level diff (default) or character-level diff
 * @returns Array of segments with highlighting information
 */
export const getTextDifferences = (
  originalText: string,
  newText: string,
): HighlightSegment[] => {
  if (!originalText && !newText) {
    return [];
  }

  if (!originalText) {
    return [{ text: newText, isChanged: true, type: 'added' }];
  }

  if (!newText) {
    return [{ text: originalText, isChanged: true, type: 'removed' }];
  }

  // Use word-level diff by default for better readability
  const diffs = diffWords(originalText, newText);

  const segments: HighlightSegment[] = [];

  diffs.forEach((diff) => {
    if (diff.added) {
      segments.push({
        text: diff.value,
        isChanged: true,
        type: 'added',
      });
    } else if (diff.removed) {
      // For removed text, we'll show it in the original but not in the new
      // This is handled by the component logic
    } else {
      segments.push({
        text: diff.value,
        isChanged: false,
        type: 'unchanged',
      });
    }
  });

  return segments;
};
